import { Controller } from '@hotwired/stimulus'

// Progresso da trilha ("feito"), guardado no banco por cliente e passo.
// O servidor manda o que está liberado (data-trilha-liberados) e o que já foi
// feito (data-trilha-feitos); marcar e desmarcar chamam a rota e a UI muda
// na hora (otimista), voltando atrás se o servidor recusar.
// Progresso antigo do localStorage (chave por entitlement) é enviado uma vez
// ao servidor e apagado.
const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

async function enviar(url, method, body) {
  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
    body: body ? JSON.stringify(body) : undefined
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.json()
}

export default class extends Controller {
  static targets = ['concluir', 'concluirOk', 'iconeOk', 'desfazer']
  static values = {
    chave: String, liberados: Array, feitos: Array, total: Number, passo: Number,
    proximoUrl: String, inicioUrl: String, marcarUrl: String, importarUrl: String
  }

  connect() {
    this.pinta()
    this.importaAntigo()
  }

  get feitos() {
    return new Set(this.feitosValue.filter((n) => this.liberadosValue.includes(n)))
  }

  set feitos(conjunto) {
    this.feitosValue = [...conjunto].sort((a, b) => a - b)
  }

  feitosValueChanged() { if (this.element.isConnected) this.pinta() }

  // Migração única: o que estava no localStorage e ainda não está no banco.
  async importaAntigo() {
    if (!this.chaveValue || !this.importarUrlValue) return
    let antigos = []
    try { antigos = JSON.parse(localStorage.getItem(this.chaveValue) || '[]') } catch { return }
    if (!Array.isArray(antigos)) antigos = []
    const faltam = antigos.map(Number).filter((n) => this.liberadosValue.includes(n) && !this.feitosValue.includes(n))
    if (!faltam.length) {
      try { localStorage.removeItem(this.chaveValue) } catch {}
      return
    }
    try {
      const { feitos } = await enviar(this.importarUrlValue, 'POST', { days: faltam })
      this.feitosValue = feitos
      try { localStorage.removeItem(this.chaveValue) } catch {}
    } catch {
      // fica no localStorage e tenta de novo na próxima visita
    }
  }

  get atual() {
    const feitos = this.feitos
    return this.liberadosValue.find((n) => !feitos.has(n)) || null
  }

  pinta() {
    const feitos = this.feitos
    const qtd = feitos.size
    const total = this.totalValue
    const pct = `${Math.round((qtd / total) * 100)}%`
    const atual = this.atual

    this.element.querySelectorAll('[data-trilha-contagem]').forEach((el) => { el.textContent = `${qtd} de ${total} feitos` })
    this.element.querySelectorAll('[data-trilha-contagem-curta]').forEach((el) => { el.textContent = `${qtd} de ${total}` })
    this.element.querySelectorAll('[data-trilha-barra]').forEach((el) => {
      el.style.width = pct
      el.parentElement.setAttribute('aria-valuenow', qtd)
    })

    // "Continuar de onde parou": passo atual, espera do próximo ou concluída
    const heroes = this.element.querySelectorAll('[data-trilha-hero]')
    if (heroes.length) {
      const alvo = atual ? String(atual) : (qtd >= total ? 'concluido' : 'espera')
      heroes.forEach((h) => { h.hidden = h.dataset.trilhaHero !== alvo })
      this.element.querySelectorAll('[data-trilha-comecar]').forEach((el) => { el.textContent = qtd === 0 ? 'Começar' : 'Continuar' })
    }

    // fileira de capas
    this.element.querySelectorAll('[data-trilha-day]').forEach((item) => {
      const n = Number(item.dataset.trilhaDay)
      const ok = feitos.has(n)
      item.querySelectorAll('.selo.ok').forEach((s) => { s.hidden = !ok })
      item.querySelectorAll('[data-trilha-target="caneta"]').forEach((c) => { c.hidden = n !== atual })
      const barra = item.querySelector('[data-trilha-item-barra]')
      if (barra) barra.style.width = ok ? '100%' : '0'
      const status = item.querySelector('[data-trilha-status]')
      if (status) {
        status.textContent = ok ? 'Feito' : n === atual ? 'Você está aqui' : 'Liberado'
        status.classList.toggle('agora', !ok)
      }
      const simples = item.querySelector('[data-trilha-status-simples]')
      if (simples) simples.textContent = ok ? 'Feito' : 'Liberado'
    })

    // lista dos 7 passos (página do passo)
    this.element.querySelectorAll('[data-trilha-linha]').forEach((linha) => {
      const n = Number(linha.dataset.trilhaLinha)
      const aqui = n === this.passoValue
      const ok = feitos.has(n)
      const sub = linha.querySelector('[data-trilha-sub]')
      if (sub) sub.textContent = aqui ? 'Você está aqui' : ok ? 'Feito' : 'Liberado'
      const icone = linha.querySelector('[data-trilha-icone]')
      if (!icone || aqui) return
      if (!icone.dataset.original) icone.dataset.original = icone.innerHTML
      icone.innerHTML = ok && this.hasIconeOkTarget ? this.iconeOkTarget.innerHTML : icone.dataset.original
    })

    // botão "Concluir passo"
    if (this.hasConcluirTarget) {
      const n = this.passoValue
      const feito = feitos.has(n)
      const prox = this.proximoUrlValue && !feitos.has(n + 1)
      this.concluirTarget.textContent = feito ? 'Passo feito ✓' : prox ? `Concluir e ir para o Passo ${n + 1}` : '✓ Concluir passo'
      this.concluirTarget.disabled = feito
      this.concluirTarget.classList.toggle('feito', feito)
      if (this.hasDesfazerTarget) this.desfazerTarget.hidden = !feito
    }
  }

  async concluir() {
    const n = this.passoValue
    if (this.feitos.has(n) || this.ocupado) return
    this.ocupado = true
    const antes = this.feitosValue
    this.feitos = new Set([...this.feitos, n])
    const espera = new Promise((resolve) => setTimeout(resolve, 1200))

    const ok = this.concluirOkTarget
    ok.hidden = false
    ok.textContent = this.proximoUrlValue ? `Passo ${n} feito. Abrindo o Passo ${n + 1}…` : `Passo ${n} feito.`

    try {
      const { feitos } = await enviar(this.marcarUrlValue, 'POST')
      this.feitosValue = feitos
    } catch {
      this.feitosValue = antes
      ok.textContent = 'Não deu para salvar agora. Confira a internet e toque de novo.'
      this.ocupado = false
      return
    }
    this.ocupado = false

    if (this.proximoUrlValue) {
      await espera
      window.Turbo ? window.Turbo.visit(this.proximoUrlValue) : (window.location.href = this.proximoUrlValue)
    } else {
      const fim = this.feitos.size >= this.totalValue
      ok.textContent = `Passo ${n} feito.${fim ? ' Trilha concluída.' : n < this.totalValue ? ` O Passo ${n + 1} libera amanhã.` : ''} `
      const a = document.createElement('a')
      a.href = this.inicioUrlValue
      a.textContent = 'Voltar ao início'
      ok.appendChild(a)
      setTimeout(() => { window.location.href = this.inicioUrlValue }, 2500)
    }
  }

  async desfazer() {
    const n = this.passoValue
    if (!this.feitos.has(n) || this.ocupado) return
    this.ocupado = true
    const antes = this.feitosValue
    const sem = this.feitos
    sem.delete(n)
    this.feitos = sem
    this.concluirOkTarget.hidden = true

    try {
      const { feitos } = await enviar(this.marcarUrlValue, 'DELETE')
      this.feitosValue = feitos
    } catch {
      this.feitosValue = antes
      this.concluirOkTarget.hidden = false
      this.concluirOkTarget.textContent = 'Não deu para desmarcar agora. Tente de novo.'
    }
    this.ocupado = false
  }
}
